import React, { Component } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { createVault, deleteVault, getVaultShareUsers, getVaults, shareVault, unshareVault, updateSelectedVault, updateVault } from '../../api/backend';
import { Application } from '../../core/app';
import { escapeHtml } from '../../utils/sanitize';

class VaultManagement extends Component {
  state = {
    vaults: [],
    shareUsers: [],
    selectedVaultId: '',
    newVaultName: '',
    editVaultName: '',
    selectedShareUserId: '',
    showDeleteModal: false,
    vaultToDelete: null,
    isLoading: true,
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.setState({ isLoading: true });
    Promise.all([getVaults(), getVaultShareUsers()]).then(([vaultRes, usersRes]) => {
      const vaults = vaultRes.data || [];
      const selectedVaultId = this.state.selectedVaultId || (vaults[0] ? String(vaults[0].id) : '');
      const selectedVault = vaults.find((vault) => String(vault.id) === String(selectedVaultId));

      this.setState({
        vaults,
        shareUsers: usersRes.data || [],
        selectedVaultId,
        editVaultName: selectedVault?.name || '',
        isLoading: false,
      });
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  getSelectedVault = () => {
    return this.state.vaults.find((vault) => String(vault.id) === String(this.state.selectedVaultId));
  }

  selectVault = (vaultId) => {
    const selectedVault = this.state.vaults.find((vault) => String(vault.id) === String(vaultId));
    this.setState({ selectedVaultId: vaultId, editVaultName: selectedVault?.name || '', selectedShareUserId: '' });
  }

  handleCreateVault = () => {
    const name = this.state.newVaultName.trim();
    if (!name) return;

    this.setState({ isLoading: true });
    createVault({ name }).then(() => {
      this.setState({ newVaultName: '' });
      this.loadData();
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  handleRenameVault = () => {
    const selectedVault = this.getSelectedVault();
    const name = this.state.editVaultName.trim();
    if (!selectedVault || !name) return;

    this.setState({ isLoading: true });
    updateVault({ id: selectedVault.id, name }).then(() => {
      this.loadData();
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  handleShareVault = () => {
    const selectedVault = this.getSelectedVault();
    if (!selectedVault || !this.state.selectedShareUserId) return;

    this.setState({ isLoading: true });
    shareVault({ vault_id: selectedVault.id, target_user_id: this.state.selectedShareUserId }).then(() => {
      this.setState({ selectedShareUserId: '' });
      this.loadData();
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  handleUnshareVault = (userId) => {
    const selectedVault = this.getSelectedVault();
    if (!selectedVault || !userId) return;

    this.setState({ isLoading: true });
    unshareVault({ vault_id: selectedVault.id, target_user_id: userId }).then(() => {
      this.loadData();
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  handleDeleteOpen = () => {
    const selectedVault = this.getSelectedVault();
    if (!selectedVault) return;
    this.setState({ showDeleteModal: true, vaultToDelete: selectedVault });
  }

  handleDeleteClose = () => {
    this.setState({ showDeleteModal: false, vaultToDelete: null });
  }

  handleDeleteVault = () => {
    const vault = this.state.vaultToDelete;
    if (!vault) return;

    this.setState({ isLoading: true, showDeleteModal: false });
    deleteVault(vault.id).then(() => {
      if (String(Application.getSelectedVault()) === String(vault.id)) {
        Application.setSelectedVault('0');
        return updateSelectedVault('0');
      }
      return null;
    }).then(() => {
      this.setState({ selectedVaultId: '', vaultToDelete: null });
      this.loadData();
    }).catch(() => {
      this.setState({ isLoading: false });
    });
  }

  render() {
    const selectedVault = this.getSelectedVault();
    const canManageSelectedVault = !!selectedVault?.can_manage;
    const sharedUserIds = (selectedVault?.shared_users || []).map((user) => String(user.id));
    const shareableUsers = this.state.shareUsers.filter((user) => !sharedUserIds.includes(String(user.id)) && String(user.id) !== String(selectedVault?.user_id));

    return (
      <div className='app-background'>
        {this.state.isLoading && (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998 }}></div>
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, backgroundColor: 'rgba(33,33,33,0.9)', padding: '20px', borderRadius: '10px' }}>
              <CircularProgress size={50} />
            </div>
          </>
        )}

        <Dialog open={this.state.showDeleteModal} onClose={this.handleDeleteClose}>
          <DialogTitle>Delete Vault</DialogTitle>
          <DialogContent>
            <p>Delete <strong>{this.state.vaultToDelete ? escapeHtml(this.state.vaultToDelete.name) : ''}</strong> and all records inside it?</p>
          </DialogContent>
          <DialogActions>
            <button className='btn btn-secondary' onClick={this.handleDeleteClose}>Cancel</button>
            <button className='btn btn-danger' onClick={this.handleDeleteVault}>Delete</button>
          </DialogActions>
        </Dialog>

        <div className='main-shopping-container settings-page'>
          <div className='main-item-container'>
            <div className='settings-header'>
              <h2>Vault Management</h2>
              <div className='settings-subtitle'>Create, rename, share, and delete vaults.</div>
            </div>

            <div className='settings-section'>
              <div className='settings-section-title'>Create Vault</div>
              <div className='vault-management-row'>
                <div className='add-item-box add-item-box--field'>
                  <input
                    value={this.state.newVaultName}
                    onChange={(event) => this.setState({ newVaultName: event.target.value })}
                    className='add-item-input'
                    placeholder='New vault name'
                  />
                </div>
                <button type='button' className='settings-action-button' onClick={this.handleCreateVault} disabled={!this.state.newVaultName.trim()}>
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Create</span>
                </button>
              </div>
            </div>

            <div className='settings-section'>
              <div className='settings-section-title'>Select Vault</div>
              <div className='add-item-box add-item-box--field'>
                <select
                  value={this.state.selectedVaultId}
                  onChange={(event) => this.selectVault(event.target.value)}
                  className='add-item-input'
                >
                  <option value=''>Select vault</option>
                  {this.state.vaults.map((vault) => (
                    <option key={vault.id} value={vault.id}>{escapeHtml(vault.name)}</option>
                  ))}
                </select>
              </div>
            </div>

            {selectedVault && canManageSelectedVault && (
              <>
                <div className='settings-section'>
                  <div className='settings-section-title'>Rename Vault</div>
                  <div className='vault-management-row'>
                    <div className='add-item-box add-item-box--field'>
                      <input
                        value={this.state.editVaultName}
                        onChange={(event) => this.setState({ editVaultName: event.target.value })}
                        className='add-item-input'
                      />
                    </div>
                    <button type='button' className='settings-action-button' onClick={this.handleRenameVault} disabled={!this.state.editVaultName.trim()}>
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Rename</span>
                    </button>
                  </div>
                </div>

                <div className='settings-section'>
                  <div className='settings-section-title'>Share Vault</div>
                  <div className='vault-management-row'>
                    <div className='add-item-box add-item-box--field'>
                      <select
                        value={this.state.selectedShareUserId}
                        onChange={(event) => this.setState({ selectedShareUserId: event.target.value })}
                        className='add-item-input'
                      >
                        <option value=''>Select user</option>
                        {shareableUsers.map((user) => (
                          <option key={user.id} value={user.id}>{escapeHtml(user.name || user.uid)}</option>
                        ))}
                      </select>
                    </div>
                    <button type='button' className='settings-action-button' onClick={this.handleShareVault} disabled={!this.state.selectedShareUserId}>
                      <FontAwesomeIcon icon={faShareAlt} />
                      <span>Share</span>
                    </button>
                  </div>
                </div>

                <div className='settings-section'>
                  <div className='settings-section-title'>Shared With</div>
                  <div className='vault-shared-user-list'>
                    {(selectedVault.shared_users || []).length ? selectedVault.shared_users.map((sharedUser) => {
                      const isOwner = String(sharedUser.id) === String(selectedVault.user_id);
                      return (
                        <div key={sharedUser.id} className='vault-shared-user-row'>
                          <div>
                            <strong>{escapeHtml(sharedUser.name || 'Unnamed user')}</strong>
                            <div>{escapeHtml(sharedUser.uid)}</div>
                          </div>
                          {isOwner ? (
                            <span className='vault-owner-pill'>Owner</span>
                          ) : (
                            <button type='button' className='vault-remove-share-button' onClick={() => this.handleUnshareVault(sharedUser.id)}>
                              Remove
                            </button>
                          )}
                        </div>
                      );
                    }) : (
                      <div className='user-empty-note'>Not shared with anyone yet</div>
                    )}
                  </div>
                </div>

                <div className='settings-section vault-danger-zone'>
                  <div>
                    <strong>Delete Vault</strong>
                    <div>This deletes {escapeHtml(selectedVault.name)} and all records inside it.</div>
                  </div>
                  <button type='button' className='btn btn-danger' onClick={this.handleDeleteOpen}>
                    <FontAwesomeIcon icon={faTrash} />
                    <span>Delete</span>
                  </button>
                </div>
              </>
            )}

            {selectedVault && !canManageSelectedVault && (
              <div className='settings-section'>
                <div className='user-empty-note'>This vault is shared with you. Only the owner can manage it.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default VaultManagement;
